/**
 * PATH: packages/drafting/src/canvas/DraftCanvas.tsx
 * FILE_ID: DRAFTING-CANVAS-DRAFTCANVAS-001
 * PURPOSE: The Konva drawing surface — paper + grid + scale bar, renders
 *   IR entities, handles select/move/resize and tool-based creation.
 * WHY: One canvas, IR-driven (scope §4). Entities live in drawing-unit
 *   coords on a scaled Konva layer so geometry == IR (no px<->unit drift).
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Stage, Layer, Rect, Line, Image as KonvaImage } from 'react-konva'
import type Konva from 'konva'
import type { DrawingIR, Entity, EntityInput, Point } from '../ir/schema'
import { snapPoint } from '../ir/normalize'
import { paperSizeMm, buildGridLines, scaleBarSpec } from './geometry'
import { EntityShape } from './entities/EntityShape'
import { SelectionTransformer } from './SelectionTransformer'
import { DraftPreview, type DraftState } from './DraftPreview'
import { useCanvasView } from './useCanvasView'
import type { Tool } from '../hooks/useDrafting'

export interface DraftCanvasProps {
  ir: DrawingIR
  tool: Tool
  activeLayerId: string
  selectedId: string | null
  backgroundUrl?: string | null
  onSelect: (id: string | null) => void
  onAddEntity: (partial: EntityInput) => string
  onUpdateEntity: (e: Entity) => void
  stageRef?: React.MutableRefObject<Konva.Stage | null>
}

type Draft = DraftState

export function DraftCanvas(props: DraftCanvasProps) {
  const { ir, tool, activeLayerId, selectedId, backgroundUrl, onSelect, onAddEntity, onUpdateEntity, stageRef } = props
  const wrapRef = useRef<HTMLDivElement | null>(null)
  const layerRef = useRef<Konva.Layer | null>(null)
  const localStage = useRef<Konva.Stage | null>(null)
  const [size, setSize] = useState({ w: 800, h: 600 })
  const [draft, setDraft] = useState<Draft | null>(null)
  const [bgImg, setBgImg] = useState<HTMLImageElement | null>(null)

  const paper = paperSizeMm(ir.meta.paper)
  const grid = buildGridLines(paper, ir.meta.grid.size)
  const { view, setView, onWheelZoom, fit } = useCanvasView(paper, size)

  // Measure container (drives fit + zoom math via useCanvasView).
  useLayoutEffect(() => {
    const el = wrapRef.current
    if (!el) return
    const ro = new ResizeObserver(() => setSize({ w: el.clientWidth, h: el.clientHeight }))
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  // Load optional background sketch (client-side only in P1).
  useEffect(() => {
    if (!backgroundUrl) {
      setBgImg(null)
      return
    }
    const img = new Image()
    img.onload = () => setBgImg(img)
    img.src = backgroundUrl
  }, [backgroundUrl])

  const setStageNode = useCallback(
    (node: Konva.Stage | null) => {
      localStage.current = node
      if (stageRef) stageRef.current = node
    },
    [stageRef],
  )

  const pointer = useCallback((): Point | null => {
    const layer = layerRef.current
    if (!layer) return null
    const p = layer.getRelativePointerPosition()
    if (!p) return null
    const raw: Point = [p.x, p.y]
    return ir.meta.grid.snap ? snapPoint(raw, ir.meta.grid.size) : raw
  }, [ir.meta.grid.snap, ir.meta.grid.size])

  const commitDraft = useCallback(
    (start: Point, end: Point) => {
      switch (tool) {
        case 'rect': {
          const x = Math.min(start[0], end[0])
          const y = Math.min(start[1], end[1])
          const w = Math.abs(end[0] - start[0])
          const h = Math.abs(end[1] - start[1])
          if (w < 1 || h < 1) return
          onAddEntity({ type: 'rect', layer: activeLayerId, x, y, w, h })
          break
        }
        case 'circle': {
          const r = Math.hypot(end[0] - start[0], end[1] - start[1])
          if (r < 1) return
          onAddEntity({ type: 'circle', layer: activeLayerId, cx: start[0], cy: start[1], r })
          break
        }
        case 'line':
          onAddEntity({ type: 'line', layer: activeLayerId, a: start, b: end })
          break
        case 'dimension':
          onAddEntity({ type: 'dimension', layer: activeLayerId, a: start, b: end })
          break
        case 'leader':
          onAddEntity({ type: 'leader', layer: activeLayerId, from: start, to: end, text: 'Note' })
          break
        default:
          break
      }
    },
    [tool, activeLayerId, onAddEntity],
  )

  const handleDown = useCallback(() => {
    const p = pointer()
    if (!p) return
    if (tool === 'select') return
    if (tool === 'text') {
      const text = window.prompt('Text label:')
      if (text) onAddEntity({ type: 'text', layer: activeLayerId, at: p, text, size: 12 })
      return
    }
    setDraft({ kind: tool, start: p, current: p })
  }, [pointer, tool, activeLayerId, onAddEntity])

  const handleMove = useCallback(() => {
    if (!draft) return
    const p = pointer()
    if (p) setDraft((d) => (d ? { ...d, current: p } : d))
  }, [draft, pointer])

  const handleUp = useCallback(() => {
    if (!draft) return
    commitDraft(draft.start, draft.current)
    setDraft(null)
  }, [draft, commitDraft])

  const handleStageClick = useCallback(
    (e: Konva.KonvaEventObject<MouseEvent>) => {
      // Click on empty stage (the background Rect or Stage) deselects.
      const name = e.target?.name?.()
      if (tool === 'select' && (e.target === e.target.getStage() || name === '__bg__' || name === '__paper__')) {
        onSelect(null)
      }
    },
    [tool, onSelect],
  )

  const handleWheel = useCallback(
    (e: Konva.KonvaEventObject<WheelEvent>) => {
      e.evt.preventDefault()
      const stage = e.target.getStage()
      const p = stage?.getPointerPosition()
      if (p) onWheelZoom(e.evt.deltaY, p)
    },
    [onWheelZoom],
  )

  // Double-click a labelled entity (text/leader/dimension) to edit it.
  const handleDblClick = useCallback(
    (e: Konva.KonvaEventObject<MouseEvent | TouchEvent>) => {
      let node: Konva.Node | null = e.target
      const ids = new Set(ir.entities.map((x) => x.id))
      while (node && !(node.id() && ids.has(node.id()))) node = node.getParent() as Konva.Node | null
      const id = node?.id()
      const ent = id ? ir.entities.find((x) => x.id === id) : undefined
      if (!ent) return
      if (ent.type === 'text' || ent.type === 'leader') {
        const next = window.prompt('Edit label:', ent.text)
        if (next != null) onUpdateEntity({ ...ent, text: next })
      } else if (ent.type === 'dimension') {
        const next = window.prompt('Dimension value (blank = auto):', ent.value != null ? String(ent.value) : '')
        if (next != null) {
          const n = Number(next)
          onUpdateEntity({ ...ent, value: next.trim() && Number.isFinite(n) ? n : undefined })
        }
      }
    },
    [ir.entities, onUpdateEntity],
  )

  const layerOf = useCallback((id: string) => ir.layers.find((l) => l.id === id), [ir.layers])
  const selectedEntity = selectedId ? ir.entities.find((e) => e.id === selectedId) : undefined
  const bar = scaleBarSpec(ir.meta.scale.ratio, ir.meta.units, view.scale)

  return (
    <div ref={wrapRef} className="relative h-full w-full overflow-hidden bg-slate-100 dark:bg-slate-800">
      <Stage
        ref={setStageNode}
        width={size.w}
        height={size.h}
        onMouseDown={(e) => {
          handleStageClick(e)
          handleDown()
        }}
        onMouseMove={handleMove}
        onMouseUp={handleUp}
        onWheel={handleWheel}
        onDblClick={handleDblClick}
        onDblTap={handleDblClick}
        style={{ cursor: tool === 'select' ? 'default' : 'crosshair' }}
      >
        <Layer
          ref={layerRef}
          x={view.x}
          y={view.y}
          scaleX={view.scale}
          scaleY={view.scale}
          // Pan: in select mode, dragging empty space (the paper, not a
          // shape) drags the whole layer. Shapes capture their own drag so
          // moving an entity still works; a click (no movement) deselects.
          draggable={tool === 'select' && !draft}
          onDragEnd={(e) => {
            // dragend bubbles from child shapes too — only the LAYER's own
            // drag is a pan (shapes update their geometry via onChange).
            const target = e.target as unknown as Konva.Node
            if (target === layerRef.current) {
              setView({ scale: view.scale, x: target.x(), y: target.y() })
            }
          }}
        >
          {/* paper */}
          <Rect name="__paper__" x={0} y={0} width={paper.w} height={paper.h} fill="#ffffff" stroke="#cbd5e1" strokeWidth={1} strokeScaleEnabled={false} shadowColor="#000" shadowBlur={6} shadowOpacity={0.08} />
          {/* reference sketch underlay (P1: client-side trace aid) */}
          {bgImg ? (
            <KonvaImage image={bgImg} x={0} y={0} width={paper.w} height={paper.h} opacity={0.45} listening={false} />
          ) : null}
          {/* grid */}
          {grid.map((g, i) => (
            <Line key={`g${i}`} points={g.points} stroke="#e2e8f0" strokeWidth={0.5} strokeScaleEnabled={false} listening={false} />
          ))}
          {/* entities */}
          {ir.entities.map((e) =>
            layerOf(e.layer)?.visible === false ? null : (
              <EntityShape
                key={e.id}
                entity={e}
                layer={layerOf(e.layer)}
                selected={selectedId === e.id}
                draggable={tool === 'select'}
                onSelect={onSelect}
                onChange={onUpdateEntity}
              />
            ),
          )}
          {/* live creation preview */}
          {draft ? <DraftPreview draft={draft} /> : null}
          {/* resize handles for the selected rect/circle/text */}
          {tool === 'select' ? (
            <SelectionTransformer selected={selectedEntity} layerRef={layerRef} onChange={onUpdateEntity} />
          ) : null}
        </Layer>
      </Stage>

      {bar ? (
        <div className="pointer-events-none absolute bottom-3 left-3 flex flex-col items-start gap-0.5">
          <div className="h-1.5 bg-slate-700 dark:bg-slate-200" style={{ width: bar.drawingLength * view.scale }} />
          <span className="text-[11px] font-medium text-slate-600 dark:text-slate-300">
            {bar.label} · scale {ir.meta.scale.ratio}
          </span>
        </div>
      ) : null}

      <button
        type="button"
        onClick={fit}
        title="Fit drawing to view"
        className="absolute bottom-3 right-3 rounded-md border border-slate-300 bg-white/90 px-2 py-1 text-[11px] font-medium text-slate-600 shadow-sm hover:bg-white dark:border-slate-600 dark:bg-slate-900/90 dark:text-slate-300"
      >
        {Math.round(view.scale * 100)}% · Fit
      </button>
    </div>
  )
}
